import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { Buttons } from 'shared';
import { useTranslation } from 'hooks/useTranslation';
import { useValidation } from 'hooks';
import { Country } from 'ts/Country';
import { ControlledCheckboxField } from 'shared/forms/ControlledCheckboxField';

import useAuth from 'features/auth/hooks/useAuth';
import { CompanySizeType, RegistrationInput } from 'features/auth/ts';
import { authPaths } from 'features/auth/routes/paths';
import { SelectInputField } from 'features/app/components/SelectInputField';
import countries from 'features/dashboard/constants/countries.json';
import { AuthInputField } from 'features/auth/components/AuthInputField';
import { TermsAndConditionsLabel } from 'features/auth/components/TermsAndConditionsLabel';

import { useStyles } from './styles';
import i18n from 'locales/i18n';
import { registerFieldValidation } from 'features/auth/api';

const mappedCountries = (countries as Country[]).map(({ code, name }, index) => ({
  id: index,
  code,
  label: name,
}));

type Props = {
  onSubmit: () => void;
};

export const SignUpForm: React.FC<Props> = ({ onSubmit }) => {
  const direction = i18n.dir();

  const classes = useStyles({ direction });
  const { t } = useTranslation('auth');
  const { error } = useAuth();
  const { phoneNumber, required, email } = useValidation();

  const [realTimeValidationLoadings, setRealTimeValidationLoadings] = useState<{
    email: boolean;
    company_name: boolean;
  }>({ email: false, company_name: false });

  const orderedFieldNames = [
    'first_name',
    'last_name',
    'email',
    'phone_number',
    'company_name',
    'country_of_business',
    'company_size',
    'is_user_agree_with_terms_and_conditions',
  ];

  const companySizeVariants = [
    {
      label: `1 ${t('to', { ns: 'app' }).toLowerCase()} 10`,
      code: CompanySizeType.FROM_1_TO_10_EMPLOYEES,
    },
    {
      label: `11 ${t('to', { ns: 'app' }).toLowerCase()} 100`,
      code: CompanySizeType.FROM_11_TO_100_EMPLOYEES,
    },
    {
      label: `101 ${t('to', { ns: 'app' }).toLowerCase()} 500`,
      code: CompanySizeType.FROM_101_TO_500_EMPLOYEES,
    },
    {
      label: `500+`,
      code: CompanySizeType.MORE_THAN_500_EMPLOYEES,
    },
  ];

  const {
    control,
    setError,
    setFocus,
    formState: { errors },
    getValues,
    watch,
  } = useFormContext();

  const handlePhoneNumberChange = () => {
    if (!getValues('phone_number')?.includes('+'))
      setError('phone_number', { message: t('phoneNumberValidationMessage', { ns: 'validation' }) });
  };

  const onNextButtonClick = async () => {
    if (realTimeValidationLoadings.email || realTimeValidationLoadings.company_name) return;
    await onSubmit();
    if (!_.isEmpty(errors)) {
      for (const fieldName of orderedFieldNames) {
        if (fieldName in errors) {
          setFocus(fieldName);
          break;
        }
      }
    }
  };

  const validateFieldFromServer = useCallback(
    async (fieldName: 'email' | 'company_name', value: string) => {
      let errorMsg = '';
      try {
        setRealTimeValidationLoadings(value => ({ ...value, [fieldName]: true }));
        await registerFieldValidation({ fieldName, value });
      } catch (e) {
        errorMsg = e.response?.data?.[0];
      }
      setRealTimeValidationLoadings(value => ({ ...value, [fieldName]: false }));
      if (errorMsg) {
        setError(fieldName, { message: errorMsg });
        return errorMsg;
      }
      return undefined;
    },
    [setError]
  );

  const debounceValidateFieldFromServer = useMemo(
    () =>
      _.debounce(
        async (fieldName: 'email' | 'company_name', value: string) => validateFieldFromServer(fieldName, value),
        300
      ),
    [validateFieldFromServer]
  );

  useEffect(() => {
    if (!error || typeof error !== 'object') return;
    const validationError = error as Record<string, unknown[]>;
    const errorKeys = Object.keys(validationError) as Array<keyof RegistrationInput>;
    errorKeys.forEach((errorKey: keyof RegistrationInput) => {
      setError(errorKey, { message: validationError[errorKey]?.join('\n') });
    });
  }, [error, setError]);

  const isCountryOfBusinessSelected = !!watch('country_of_business');
  const isCompanySizeSelected = !!watch('company_size');

  return (
    <Box display="flex" flexDirection="column" className={classes.baseSignUpForm}>
      <Box display="flex" width="100%">
        <Box className={classes.firstNameInputField}>
          <AuthInputField
            required
            bordered={false}
            control={control}
            type="text"
            name="first_name"
            fullWidth
            label={t('firstName')}
            rules={{
              validate: {
                ...required,
              },
              pattern: {
                message: t('numbersUnacceptable', { ns: 'validation' }),
                value: /^[^0-9.]+$/g,
              },
            }}
          />
        </Box>
        <Box className={classes.lastNameInputField}>
          <AuthInputField
            required
            bordered={false}
            control={control}
            type="text"
            name="last_name"
            fullWidth
            label={t('lastName')}
            rules={{
              validate: {
                ...required,
              },
              pattern: {
                message: t('numbersUnacceptable', { ns: 'validation' }),
                value: /^[^0-9.]+$/g,
              },
            }}
          />
        </Box>
      </Box>

      <AuthInputField
        required
        control={control}
        bordered={false}
        type="email"
        name="email"
        fullWidth
        label={t('workEmailAddress')}
        isLoading={realTimeValidationLoadings.email}
        onChange={async ({ target: { value } }) => {
          if (value && !email.email(value)) {
            await debounceValidateFieldFromServer('email', value);
          }
        }}
        rules={{
          validate: {
            ...required,
            email: async value => {
              return email.email(value) || (await validateFieldFromServer('email', value));
            },
          },
        }}
      />

      <AuthInputField
        required
        control={control}
        bordered={false}
        type="text"
        name="phone_number"
        fullWidth
        label={t('phoneNumber')}
        onChange={handlePhoneNumberChange}
        rules={{
          validate: {
            ...required,
            phone_number: phoneNumber,
          },
        }}
      />

      <AuthInputField
        required
        control={control}
        bordered={false}
        type="text"
        name="company_name"
        fullWidth
        label={t('companyName')}
        isLoading={realTimeValidationLoadings.company_name}
        onChange={async ({ target: { value } }) => {
          if (value) {
            await debounceValidateFieldFromServer('company_name', value);
          }
        }}
        rules={{
          validate: {
            ...required,
            company_name: async value => await validateFieldFromServer('company_name', value),
          },
        }}
        helperText={errors && errors?.company_name?.message}
      />

      <Box position="relative">
        <SelectInputField
          control={control}
          isClearable={true}
          name="country_of_business"
          label={t('countryOfBusiness')}
          options={mappedCountries}
          placeholder={''}
          className={clsx(classes.selectField, {
            [classes.selectedSelectField]: isCountryOfBusinessSelected,
          })}
          helperTextClass={classes.selectHelperText}
          additionalLabelClass={classes.selectFieldLabel}
          rules={{
            validate: {
              ...required,
            },
          }}
        />
      </Box>

      <Box position="relative">
        <SelectInputField
          control={control}
          isClearable={true}
          name="company_size"
          label={t('companySize')}
          options={companySizeVariants}
          placeholder={''}
          className={clsx(classes.selectField, {
            [classes.selectedSelectField]: isCompanySizeSelected,
          })}
          helperTextClass={classes.selectHelperText}
          additionalLabelClass={classes.selectFieldLabel}
          rules={{
            validate: {
              ...required,
            },
          }}
        />
      </Box>

      <ControlledCheckboxField
        name="is_user_agree_with_terms_and_conditions"
        control={control}
        className={classes.linkLabel}
        rules={{
          validate: {
            ...required,
          },
        }}
        label={<TermsAndConditionsLabel />}
      />
      <Buttons.Auth
        title={t('signUp')}
        onClick={onNextButtonClick}
        classes={{ root: classes.nextBtn }}
        type="button"
        variant="outlined"
        fullWidth
      />
      <Typography className={classes.linkToLoginContainer}>
        {t('alreadyHaveAnAccount')} <Link to={authPaths.static.login}>{t('loginHere')}</Link>
      </Typography>
    </Box>
  );
};
